import { CircleAlert, CircleCheck, X } from "lucide-react";
import { useEffect, useSyncExternalStore, type ReactNode, type RefObject } from "react";
import { cn } from "./cn.ts";

interface ToastItem {
  id: number;
  tone: "success" | "error";
  message: string;
}

const TOAST_MS = 5_000;
const MAX_VISIBLE = 3;

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

const emit = (): void => {
  for (const listener of listeners) listener();
};

const dismiss = (id: number): void => {
  items = items.filter((item) => item.id !== id);
  emit();
};

const push = (tone: ToastItem["tone"], message: string): void => {
  const id = nextId++;
  items = [...items, { id, tone, message }].slice(-MAX_VISIBLE);
  emit();
  window.setTimeout(() => dismiss(id), TOAST_MS);
};

export const toast = {
  success: (message: string): void => push("success", message),
  error: (message: string): void => push("error", message),
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const CLEARANCE_VAR = "--ck-toast-clearance";

/**
 * Lifts the toasts above a sticky bottom bar (an editor's save bar) while it is mounted, so
 * a notification never covers the Save button; follows the bar's height as it wraps.
 */
export const useToastClearance = (ref: RefObject<HTMLElement | null>): void => {
  useEffect(() => {
    const bar = ref.current;
    if (!bar) return;
    const root = document.documentElement;
    const observer = new ResizeObserver(() => {
      const offset = Number.parseFloat(getComputedStyle(bar).bottom) || 0;
      root.style.setProperty(CLEARANCE_VAR, `${bar.offsetHeight + offset}px`);
    });
    observer.observe(bar);
    return (): void => {
      observer.disconnect();
      root.style.removeProperty(CLEARANCE_VAR);
    };
  }, [ref]);
};

interface ToasterProps {
  /** Accessible name of the notification region. */
  label: string;
  dismissLabel: string;
}

/**
 * Polite live region; mount once (CourseSite does). Bottom of the viewport, raised by
 * `useToastClearance` on pages with a sticky save bar.
 */
export const Toaster = ({ label, dismissLabel }: ToasterProps): ReactNode => {
  const visible = useSyncExternalStore(
    subscribe,
    () => items,
    () => items,
  );

  return (
    <section
      aria-label={label}
      aria-live="polite"
      className={cn(
        "pointer-events-none fixed inset-x-0 bottom-[var(--ck-toast-clearance,0px)] z-50 flex",
        "flex-col items-center gap-2 p-4 sm:items-end print:hidden",
      )}>
      {visible.map((item) => (
        <div
          key={item.id}
          className={cn(
            "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border bg-card",
            "px-4 py-3 text-card-foreground shadow-lg",
            item.tone === "success" ? "border-success/40" : "border-danger/50",
          )}>
          {item.tone === "success" ? (
            <CircleCheck aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-success" />
          ) : (
            <CircleAlert aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-danger" />
          )}
          <p className="flex-1">{item.message}</p>
          <button
            type="button"
            aria-label={dismissLabel}
            className={cn(
              "-my-2.5 -mr-3 grid size-11 shrink-0 place-items-center rounded-full",
              "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => dismiss(item.id)}>
            <X aria-hidden="true" className="size-4" />
          </button>
        </div>
      ))}
    </section>
  );
};
